import React, { useContext, useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import LayoutPage from "../../../templates/Layout/LayoutPage";
import { Grid, Box, Button, Tooltip, Menu, MenuItem, Select, Alert } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import BoardBuilder from "../../../components/organisms/BoardBuilder/BoardBuilder";
import {
    simpleFetch,
    fetchData,
    valueFormatted,
    buildOnLoad,
    resumeSalary,
    refDistrictHistogram,
    buildColumns,
    buildRows
} from "src/utils/helpsers";
import { nullText } from '../../../components/atoms/NullDisplay/NullDisplay';
import { MenuBoxStyles, BoardActionsStyles, BoardGroupCounter } from "../DistrictInsight/DistrictInsight.styles";
import { SalaryDashboardStyles, nullsBlockAccordion } from "./SalaryDashboard.styles";
import MapView from "src/components/organisms/MapView/MapView";
import LollipopChart from "../../../components/organisms/LollipopChart/LollipopChart"
import DistrictCard from "../../../components/organisms/DistrictCard/DistrictCard";
import Paper from '@mui/material/Paper';
import LineChart from "src/components/organisms/LineChart/LineChart";
import GroupDisplay from "../../../components/molecules/GroupDisplay/GroupDisplay";
import ErrorBarsChart from "src/components/organisms/ErrorBars/ErrorBars";
import DataGridTable from "../../../components/organisms/Table/DataGridTable";
import AccordionSummary from "@mui/material/AccordionSummary";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import MultiVariableFilter from "../../../components/organisms/MultiVariableFilter/MultiVariableFilter";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { TokenContext } from "../../../utils/Context";
import ReportButtons from "../../../components/organisms/BugReport/ReportButtons";

function SalaryDashboard() {
    // User session token
    const { userToken } = useContext(TokenContext);

    // User session metadata and data
    const { user } = useAuth0();
    const metadata = user[`${process.env.REACT_APP_AUTH0_KEY}/district`];
    const enabledHealthBenefits = metadata.enable_hbenefits;
    const navigate = useNavigate();

    const [referenceDistrict, setReferenceDistrict] = useState();
    const [year, setYear] = useState({});
    const [groupInfo, setGroupInfo] = useState();
    const [groupType, setGroupType] = useState();
    const [irns, setIrns] = useState();
    const [boardData, setBoardData] = useState();
    const [btnFilter, setBtnFilter] = useState(null);
    const [districtsCount, setDistrictsCount] = useState();
    const [salaryVariable, setSalaryVariable] = useState();
    const [highDensity, setHighDensity] = useState(false);
    const [loading, setLoading] = useState();
    const [mapData, setMapData] = useState([]);
    const [lollipopGroup, setLollipopGroup] = useState();
    const [histogramAccumulated, setHistogramAccumulated] = useState();
    const [varAccumulated, setVarAccumulated] = useState();
    const [statsAccumulated, setStatsAccumulated] = useState();
    const [histogramSpeed, setHistogramSpeed] = useState();
    const [varSpeed, setVarSpeed] = useState();
    const [statsSpeed, setStatsSpeed] = useState();
    const [histogramBaseSalary, setHistogramBaseSalary] = useState();
    const [varBaseSalary, setVarBaseSalary] = useState();
    const [statsBaseSalary, setStatsBaseSalary] = useState();
    const [varsFilters, setVarsFilters] = useState(null);

    // Academic level data control
    const [toggleAcadLevel, setToggleAcadLevel] = useState(false);

    // Line chart hooks
    const [lineData, setLineData] = useState();
    const [nullDistricts, setNullDistricts] = useState([]);
    const [districtHistoricalDataNull, setDistrictHistoricalDataNull] = useState(false);
    const [groupHistoricalDataNull, setGroupHistoricalDataNull] = useState(false);

    // Errorbar chart hooks (for high density chart)
    const [errorBar, setErrorBar] = useState();
    const [ranges, setRanges] = useState();
    const [steps, setSteps] = useState()
    const [districtMaxStep, setDistrictMaxStep] = useState();

    // DistrictCard data
    const [salaryDataBa, setSalaryDataBa] = useState();
    const [salaryDataMa, setSalaryDataMa] = useState();

    const openFilters = Boolean(btnFilter);

    // Table Data
    const [varsFilteredTable, setVarsFilteredTable] = useState()
    const [salaryTableDataBa, setSalaryTableDataBa] = useState();
    const [salaryTableDataMa, setSalaryTableDataMa] = useState();

    // Saving salary table data on refs to render in table based on BA/MA mode.
    const salaryTableBaRef = useRef();
    const salaryTableMaRef = useRef();

    const irnsRef = useRef();

    // Control
    const handleAcademicLevel = () => {
        setHistogramAccumulated();
        setHistogramBaseSalary();
        setHistogramSpeed();
        setToggleAcadLevel(!toggleAcadLevel)
    }

    const handleFilter = () => {
        setBtnFilter(null);
    };


    // Control Map dropdown change
    const handleChange = (event) => {
        setSalaryVariable(event.target.value);
    }

    const clickFilters = (event) => {
        setBtnFilter(event.currentTarget);
    };

    const closeFilters = () => {
        setBtnFilter(null);
    };

    // Get MapView data on first load.
    useEffect(() => {
        const fetchAbort = new AbortController();
        if (irnsRef.current && year?.value && referenceDistrict?.value) {
            let endPoint = year.value === 'MCD'
                ? `${process.env.REACT_APP_API_URL}/hvd_polygons_mcd?irn=in.(${irnsRef.current},${referenceDistrict.value})`
                : `${process.env.REACT_APP_API_URL}/hvd_polygons?irn=in.(${irnsRef.current},${referenceDistrict.value})&file_year=eq.${year.value}`;
            fetchData({
                endpoint: endPoint,
                controller: fetchAbort
            }).then(res => {
                setMapData(
                    (res.data).map(item => {
                        return {
                            ...item,
                            color: "#4646FF",
                        };
                    })
                );
            })
        }
    }, [year, referenceDistrict])

    useEffect(() => {
        if (metadata) {
            const userSessionType = metadata.hasOwnProperty("sessionType")
                ? metadata.sessionType
                : "forbidden";
            switch (userSessionType) {
                case "district-access":
                    window.localStorage.setItem("session-type", "district-access");
                    break;
                case "full-access":
                    window.localStorage.setItem("session-type", "full-access");
                    break;
                case "multiple-district-access":
                    window.localStorage.setItem(
                        "session-type",
                        "multiple-district-access"
                    );
                    break;
                case "forbidden":
                    navigate("/AccessDenied", { replace: true });
                    break;
                default:
                    navigate("/AccessDenied", { replace: true });
                    break;
            }
        } else {
            navigate("/AccessDenied", { replace: true });
        }
    }, [navigate, user, metadata]);

    useEffect(() => {
        if (boardData) {
            // Irns into an array to be iterables.
            let irnsToString = (boardData.irns).toString()
            let irnsArray = irnsToString.replaceAll(' ', '').split(',')

            setReferenceDistrict(boardData.referenceDistrict);
            setYear(boardData.year);
            setIrns(irnsArray)
            setDistrictsCount(boardData.groupCount)
            setGroupInfo(boardData.groupInfo);
            setGroupType(boardData.groupType)

            // ---- Cleaning board
            setMapData([])
            setLineData()
            setErrorBar()
            setHistogramAccumulated()
            setHistogramBaseSalary()
            setHistogramSpeed()
            setDistrictHistoricalDataNull(false)
            setGroupHistoricalDataNull(false)
            setSalaryTableDataBa()
            setSalaryTableDataMa()
            setSalaryDataBa();
            setSalaryDataMa();

            setLollipopGroup();
            setHistogramAccumulated();
            setHistogramSpeed();
            setHistogramBaseSalary();

            irnsRef.current = boardData.irns
        }
    }, [boardData]);

    useEffect(() => {
        if (userToken) {
            buildOnLoad({ user: user, callback: setBoardData, token: userToken });
        }
    }, [navigate, user, userToken])

    // Get Reference District information based on BA/MA toggle value.
    useEffect(() => {
        const fetchAbort = new AbortController();
        if (referenceDistrict?.value && year?.value) {
            fetchData({
                endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_resume_salary?_year=${year.value}&_irn=${referenceDistrict.value}`,
                controller: fetchAbort
            }).then(res => {
                let data = res.data[0];
                setSalaryDataBa(resumeSalary({data: data, level: 'ba'}))
                setSalaryDataMa(resumeSalary({data: data, level: 'ma'}))
            })
        }
        //Aborts the request when the component unmounts
        return () => fetchAbort?.abort();
    }, [referenceDistrict, year]);

    /**
     * Updating Line chart data.
     */
    useEffect(() => {
        const fetchAbort = new AbortController();
        if (referenceDistrict?.value && year?.value && irns) {
            let mode = toggleAcadLevel ? "MA" : "BA"
            fetchData({
                endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_line_chart_salary?_year=${year.value}&_acadlevel=${mode}&_irns=${irns}&_irn=${referenceDistrict.value}`,
                controller: fetchAbort
            }).then(res => {
                let districtsWithData = [];
                let nulls = [];
                if(year.value === "MCD") {
                    res.data = res.data?.map( districtSalaryData => {
                        return {
                        ...districtSalaryData,
                          // If data is from a different year that MCD current year, data should be null
                          data: districtSalaryData.year !== "2026" ? null : districtSalaryData.data
                        }
                    })
                }
                (res.data).forEach(district => {
                    if (district.data && Array.isArray(district.data)) {
                        district.data = district.data.slice(0, 38);
                    }
                    // If reference district hasn't data.
                    if(district.irn === referenceDistrict.value) {
                        setDistrictHistoricalDataNull(!district.data)
                    }
                    if (district.data) return districtsWithData.push(district)
                    return nulls.push(district)
                })
                // Adding zone to each district in the group.
                let dataWithZone = districtsWithData.map( district => {
                    let maxStep = district.max_step;
                    // Max step of the reference district
                    if(district.irn === referenceDistrict.value) {
                        setDistrictMaxStep(maxStep);
                    }
                    return {
                        ...district,
                        zoneAxis: 'x',
                        zones: [{
                            value: maxStep + 1,
                        }, {
                            color: "#c4c4c4"
                        }]
                    }
                })
                setLineData(dataWithZone)
                setNullDistricts(nulls)
                setLoading(true)
            })

            // If not irns the stats are not required
            if(irns.length) {
                fetchData({
                    endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_line_chart_salary_stats?_irns=${irns}&_acadlevel=${mode}&_year=${year.value}&_irn=${referenceDistrict.value}`,
                    controller: fetchAbort
                }).then(res => {
                    const formattedData = res.data.map(item => {
                        return [item.min_value, item.max_value]
                    })
                    if (formattedData && Array.isArray(formattedData)) {
                        let sliceData = formattedData.slice(0, 38);
                        setRanges(sliceData)
                      }

                    const steps = (res.data).map(item => item.step)
                    setSteps(steps)
                })
            }
        }
        //Aborts the request when the component unmounts
        return () => fetchAbort?.abort();
    }, [referenceDistrict, year, irns, toggleAcadLevel]);

    // Build ErrorBars Chart data.
    useEffect(() => {
        if (lineData && ranges && steps) {
            let refDistrictData = lineData.filter(district => district.irn === referenceDistrict.value);
            setGroupHistoricalDataNull(!ranges.length)
            setErrorBar({
                categories: steps,
                series: [
                    {
                        name: 'Step',
                        type: 'errorbar',
                        data: ranges,
                        lineWidth: 2,
                    },
                    {
                        name: referenceDistrict.label,
                        type: 'spline',
                        color: '#DF8500',
                        lineWidth: 1,
                        data: refDistrictData[0]?.data,
                        zoneAxis: 'x',
                        zones: [{
                            value: districtMaxStep + 1,
                        }, {
                            color: "#c4c4c4"
                        }]
                    }
                ],
            })
        }
    }, [ranges, steps, lineData, referenceDistrict, districtMaxStep]);

    useEffect(() => {
        setHighDensity(lineData?.length > 100)
        setLoading(false);
    }, [lineData]);


    useEffect(() => {
        let year_value = [2025, 2026, null, undefined, ''].includes(year.value) ? 2020 : year.value;
        if (year.value === 'MCD') {
            simpleFetch(
                `${process.env.REACT_APP_API_URL}/mcd_source_variable?value_var=in.("lifetime_earnings_ma","lifetime_earnings_ba","base_salary_ma","base_salary_ba","years_to_amount_ba","years_to_amount_ma")&visible=eq.true&order=label.asc`
            ).then((data) => {
                setLollipopGroup(data);
            });
        } else {
            simpleFetch(
                `${process.env.REACT_APP_API_URL}/hvd_source_variable?file_year=eq.${year_value}&value_var=in.("lifetime_earnings_ma","lifetime_earnings_ba","base_salary_ma","base_salary_ba","years_to_amount_ba","years_to_amount_ma")&visible=eq.true&order=label.asc`
            ).then((data) => {
                setLollipopGroup(data);
            });
        }
    }, [year]);


    /**
    * Updating Accumulate Earnings histogram chart.
    */
    useEffect(() => {
        let histogramAccumulatedData = { categories: [], data: [], district: {} };
        let levelVariable = toggleAcadLevel ? 'lifetime_earnings_ma' : 'lifetime_earnings_ba';

        if (irns && lollipopGroup && referenceDistrict) {
            let irnsUpdated = [...irns, referenceDistrict.value]
            let varAcc = lollipopGroup.filter(function (variable) { return variable.value_var === levelVariable })[0];
            setVarAccumulated(varAcc);

            // If not irns the stats are not required
            if(irns.length) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/rpc/hvd_stats?_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsAccumulated([data[0].min_var, data[0].max_var]);
                    }, 10)
                );

            }

            if (year.value === 'MCD') {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/mcd_tabloide?select=irn,x:${levelVariable},year_x:${levelVariable}_yr,district_name,irn&irn=in.(${irnsUpdated})&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if (data?.length) {
                            // Filter data different from MCD year.
                            data.filter(el => el.year_x === 2026).forEach((item) => {
                                histogramAccumulatedData["categories"].push(`${item.district_name} (${item.year_x})`);
                                if (item.irn === referenceDistrict.value) {
                                    histogramAccumulatedData["data"].push(refDistrictHistogram(item.x, data));
                                    histogramAccumulatedData["district"] = item;
                                } else {
                                    histogramAccumulatedData["data"].push(item.x);
                                }
                            })
                            setHistogramAccumulated(histogramAccumulatedData)
                        }
                    })
                    .catch((e) => setHistogramAccumulated("no-data"));
            } else {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,x:${levelVariable},district_name,irn&irn=in.(${irnsUpdated})&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if(data?.length) {
                            data.forEach((item) => {
                                histogramAccumulatedData["categories"].push(item.district_name);
                                if (item.irn === referenceDistrict.value) {
                                    histogramAccumulatedData["data"].push(refDistrictHistogram(item.x, data));
                                histogramAccumulatedData["district"] = item;
                                } else {
                                    histogramAccumulatedData["data"].push(item.x);
                                }
                            })
                            setHistogramAccumulated(histogramAccumulatedData)
                        }
                    })
                    .catch(() => setHistogramAccumulated("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleAcadLevel]);

    /**
    * Updating Speed histogram chart.
    */
    useEffect(() => {
        let histogramSpeedData = { categories: [], data: [], district: {} };
        let levelVariable = toggleAcadLevel ? 'years_to_amount_ma' : 'years_to_amount_ba';

        if (irns && lollipopGroup && referenceDistrict) {
            let irnsUpdated = [...irns, referenceDistrict.value]
            let varAcc = lollipopGroup.filter(function (variable) { return variable.value_var === levelVariable })[0];
            setVarSpeed(varAcc);

            // If not irns the stats are not required
            if(irns.length) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/rpc/hvd_stats?_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsSpeed([data[0].min_var, data[0].max_var]);
                    }, 10)
                );
            }

            if (year.value === 'MCD') {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/mcd_tabloide?select=irn,x:${levelVariable},year_x:${levelVariable}_yr,district_name,irn&irn=in.(${irnsUpdated})&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if(data?.length) {
                            // Removing all elements different from MCD year
                            data.filter(el => el.year_x === 2026).forEach((item) => {
                                histogramSpeedData["categories"].push(`${item.district_name} (${item.year_x})`);
                                if (item.irn === referenceDistrict.value) {
                                    histogramSpeedData["data"].push(refDistrictHistogram(item.x, data));
                                    histogramSpeedData["district"] = item;
                                } else {
                                    histogramSpeedData["data"].push(item.x);
                                }
                            })
                        }
                      }
                    )
                    .then(() => {
                        setHistogramSpeed(histogramSpeedData)
                    })
                    .catch(() => setHistogramSpeed("no-data"));
            } else {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,x:${levelVariable},district_name,irn&irn=in.(${irnsUpdated})&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if(data?.length) {
                            data.forEach((item) => {
                                histogramSpeedData["categories"].push(item.district_name);
                                if (item.irn === referenceDistrict.value) {
                                    histogramSpeedData["data"].push(refDistrictHistogram(item.x, data));
                                    histogramSpeedData["district"] = item;
                                } else {
                                    histogramSpeedData["data"].push(item.x);
                                }
                            })
                            setHistogramSpeed(histogramSpeedData)
                        }
                    })
                    .catch(() => setHistogramSpeed("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleAcadLevel]);

    /**
    * Updating Base Salary histogram chart.
    */
    useEffect(() => {
        let histogramBaseSalaryData = { categories: [], data: [], district: {} };
        let levelVariable = toggleAcadLevel ? 'base_salary_ma' : 'base_salary_ba';

        if (irns && lollipopGroup && referenceDistrict) {
            let irnsUpdated = [...irns, referenceDistrict.value]
            let varAcc = lollipopGroup.filter(function (variable) { return variable.value_var === levelVariable })[0];
            setVarBaseSalary(varAcc);

            // If not irns the stats are not required
            if(irns.length) {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/rpc/hvd_stats?_var=${levelVariable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year.value}`
                ).then((data) =>
                    setTimeout(() => {
                        setStatsBaseSalary([data[0].min_var, data[0].max_var]);
                    }, 10)
                );
            }

            if (year.value === 'MCD') {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/mcd_tabloide?select=irn,x:${levelVariable},year_x:${levelVariable}_yr,district_name,irn&irn=in.(${irnsUpdated})&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) =>
                      // Removing all elements different from MCD year
                        data.filter(el => el.year_x === 2026).forEach((item) => {
                            histogramBaseSalaryData["categories"].push(`${item.district_name} (${item.year_x})`);
                            if (item.irn === referenceDistrict.value) {
                                histogramBaseSalaryData["data"].push(refDistrictHistogram(item.x, data));
                                histogramBaseSalaryData["district"] = item;
                            } else {
                                histogramBaseSalaryData["data"].push(item.x);
                            }
                        })
                    )
                    .then(() => setHistogramBaseSalary(histogramBaseSalaryData))
                    .catch(() => setHistogramBaseSalary("no-data"));
            } else {
                simpleFetch(
                    `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,x:${levelVariable},district_name,irn&irn=in.(${irnsUpdated})&file_year=eq.${year.value}&${levelVariable}=not.is.null&order=${levelVariable}.desc`
                )
                    .then((data) => {
                        if (data?.length) {
                            data.forEach((item) => {
                                histogramBaseSalaryData["categories"].push(item.district_name);
                                if (item.irn === referenceDistrict.value) {
                                    histogramBaseSalaryData["data"].push(refDistrictHistogram(item.x, data));
                                    histogramBaseSalaryData["district"] = item;
                                } else {
                                    histogramBaseSalaryData["data"].push(item.x);
                                }
                            });
                            setHistogramBaseSalary(histogramBaseSalaryData)
                        }
                    })
                    .catch(() => setHistogramBaseSalary("no-data"));
            }
        }
    }, [irns, referenceDistrict, year, lollipopGroup, toggleAcadLevel]);

    const[salaryOptions, setSalaryOptions] = useState()

    useEffect(() => {
        if(lollipopGroup?.length) {
            let prefix = toggleAcadLevel ? '_ma' : '_ba'
            let options = lollipopGroup.map(el => ({
                label: el.label,
                value: el.value_var
            })).filter( el => el.value.includes(prefix))

            setSalaryOptions(options);
            setSalaryVariable(options[0].value)

        }

    }, [lollipopGroup, toggleAcadLevel]);


    /**
     *  Set columns and row for salary data table on load
     */
    useEffect(() => {
        if (referenceDistrict?.value && year?.value) {
            let irnsGroup = irnsRef.current;
            // On load check if reference district is part of the group if not, add it.
            if( !irnsGroup.includes(`${referenceDistrict.value}`)) {
                irnsGroup += `, ${referenceDistrict?.value}`
            }
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_salary_vars?_year=${year.value}&_irns=${irnsGroup}`)
                .then(data => {
                    let metadata = data[0].ba.metadata;
                    let columnsBase = [
                        {
                            field: 'irn',
                            headerName: 'IRN',
                            width: 100,
                        },
                        {
                            field: 'district_name',
                            headerName: 'District',
                            minWidth: 150,
                            flex: 1,
                        }
                    ]

                    // If year is MCD add the catalog year column.
                    if(year.value === "MCD") {
                        columnsBase = [
                          ...columnsBase,
                            {
                                field: 'catalog_year',
                                headerName: 'Catalog year',
                                width: 100,
                            }
                        ]
                    }

                    // Exclude MA column to get BA columns and base columns
                    let columnsBa = buildColumns(metadata, "_ma");
                    let rowsBa = buildRows(data[0].ba.data);
                    // Exclude BA column to get MA columns and base columns
                    let columnsMa = buildColumns(metadata, "_ba");
                    let rowsMa = buildRows(data[0].ma.data);

                    salaryTableBaRef.current = { columns: [...columnsBase, ...columnsBa], rows: rowsBa}
                    salaryTableMaRef.current = { columns: [...columnsBase, ...columnsMa], rows:rowsMa}

                    //On load BA values set
                    setSalaryTableDataBa({ columns: [...columnsBase, ...columnsBa], rows: rowsBa })
                    setSalaryTableDataMa({ columns: [...columnsBase, ...columnsMa], rows:rowsMa })
                })
        }
    }, [referenceDistrict, year]);

    /**
     * Using the data returner by the multivariable filter update hooks and build salary table.
     * @param {object} data: object returned on filters apply.
     */
    const getFiltersInformation = useRef((data) => {
        let filtersData = data.filtersData.current;
        let mapFiltered = (data.mapData).filter(d => (d.filter === 1 || d.filter === 0));

        let var2 = filtersData.vars.var2;
        if (var2) {
            mapFiltered = (data.mapData).filter(d => (d.filter === 2 || d.filter === 0));
        }
        
        setVarsFilters(filtersData.vars)

        // Structuring data to be compatible with map choropleth feature.
        let mapPolygons = mapFiltered.map(d => {
            let salary_vars = d.variables_polygons;
            delete d.variables_polygons;
            return {
                ...d,
                ...salary_vars
            }
        })

        let lastFilter = Object.keys(filtersData.vars).pop();
        let irnsUpdated = filtersData.vars[lastFilter].irns;

        setIrns(irnsUpdated);
        setMapData(mapPolygons);
        setDistrictsCount(filtersData.group_length);

        let variablesColumns = [];
        let variablesRows = [];

        let getData = Object.keys(filtersData.vars).map( async(key) => {
            let varData = filtersData.vars[key];
            /** TODO Remove varName key once the saved groups structure is updated */
            let varName = (varData['varName'] || varData['value_var']);
            let endpoint;
            // Only for health variables.
            let lbVariable;
            let planNum;
            let planType;
            if(!irnsUpdated.includes(filtersData.referenceDistrict.value)) {
                irnsUpdated.push(filtersData.referenceDistrict.value)
            }
            // Health variable validation based on dwh_table value.
            if (varData['dwh_table'] === "health_insurance_readable" && enabledHealthBenefits) {
                lbVariable = varName.split('__')[0];
                planNum = varName.split('__')[1];
                planType = varName.split('__')[2];

                endpoint = filtersData.year.value === 'MCD'
                    ? `${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=irn,district_name,${lbVariable},${lbVariable}_yr&irn=in.(${irnsUpdated})&plannum=eq.${planNum}&q13_plantype_p7q3=eq.${planType}`
                    : `${process.env.REACT_APP_API_URL}/health_insurance_readable?select=irn,district_name,${lbVariable}&irn=in.(${irnsUpdated})&file_year=eq.${filtersData.year.value}&plannum=eq.${planNum}&q13_plantype_p7q3=eq.${planType}`;
            } else {
                endpoint = filtersData.year.value === 'MCD'
                    ? `${process.env.REACT_APP_API_URL}/mcd_tabloide?select=irn,district_name,${varName},${varName}_yr&irn=in.(${irnsUpdated})`
                    : `${process.env.REACT_APP_API_URL}/hvd_tabloide?select=irn,district_name,${varName}&irn=in.(${irnsUpdated})&file_year=eq.${filtersData.year.value}`
            }

            return await simpleFetch(endpoint).then( data => {
                let newRows = data.map( el => ({...el, id: el.irn}));
                // If new row id and old row id match, merge it if not add new row.
                variablesRows = newRows.map((newRow) => {
                    let oldRow = variablesRows.find(oldRow => oldRow.id === newRow.id);
                    return oldRow ? { ...newRow, ...oldRow } : { ...newRow }
                });
                // New columns
                variablesColumns.push(
                    {
                        /** TODO Remove varName key once the saved groups structure is updated */
                        field: lbVariable ?? varName,
                        headerName: varData['label'],
                        minWidth: 150,
                        flex: 1,
                        cellClassName: (params) => params.value === null ? 'null-display' : '',
                        valueFormatter: (params) => {
                            if(params.value === null) return nullText('NISS')
                            if (typeof params.value === "number") {
                                return valueFormatted(params.value, varData['var_type'])
                            }
                        },
                    }
                )
                if(filtersData.year.value === 'MCD') {
                    variablesColumns.push({
                        field: lbVariable ? `${lbVariable}_yr` : `${varName}_yr`,
                        headerName: `${varData['label']} (Year)`,
                        cellClassName: (params) => params.value === null ? 'null-display' : '',
                        valueFormatter: (params) => {
                            if(params.value === null) return nullText('NISS')
                            return params.value
                        },
                    })
                }
            });
        })
        // After build the table data update the variables table.
        Promise.all(getData).then( data => setVarsFilteredTable({
            rows: variablesRows,
            columns: variablesColumns
        }))
    })

    useEffect(() => {
        if(varsFilteredTable){
            let updatedRowsBa = (varsFilteredTable.rows).map(newRow => {
                let oldRow = (salaryTableBaRef.current.rows).find(oldRow => oldRow.irn === newRow.irn);
                return { ...newRow, ...oldRow };
            });
            let updatedColumnsBa = [...salaryTableBaRef.current.columns, ...varsFilteredTable.columns]
            setSalaryTableDataBa({ columns: updatedColumnsBa, rows: updatedRowsBa })

            let updatedRowsMa = (varsFilteredTable.rows).map(newRow => {
                let oldRow = (salaryTableMaRef.current.rows).find(oldRow => oldRow.irn === newRow.irn);
                return { ...newRow, ...oldRow };
            });
            let updatedColumnsMa = [...salaryTableMaRef.current.columns, ...varsFilteredTable.columns]
            setSalaryTableDataMa({ columns: updatedColumnsMa, rows: updatedRowsMa })

        }
    }, [varsFilteredTable]);

    const topContentElement = (
        <Box className={"board-actions"} sx={BoardActionsStyles}>
            <Grid container className={"board-actions__wrapper"} spacing={2}>
                <Grid item xs={12} lg={3} className={"board-actions__board-menu"} display="flex">
                    <Tooltip title="Filters">
                        <Button
                            id="button-filters"
                            aria-controls={"menu-filters"}
                            aria-haspopup="true"
                            aria-expanded={openFilters ? "true" : "false"}
                            variant="contained"
                            onClick={clickFilters}
                            color={"grape"}
                            className="board-menu__filters"
                        >
                            <FilterAltOutlinedIcon />
                            Filters
                        </Button>
                    </Tooltip>
                    <Menu
                        id="menuFilters"
                        MenuListProps={{
                            "aria-labelledby": "button-filters",
                        }}
                        anchorEl={btnFilter}
                        open={openFilters}
                        onClose={closeFilters}
                        sx={MenuBoxStyles}
                    >
                        <MenuItem disableRipple>
                            <BoardBuilder
                                groupEnabled={true}
                                yearEnabled={true}
                                boardData={setBoardData}
                                onSubmit={() => handleFilter()}
                                type = "salary"
                            />
                        </MenuItem>
                    </Menu>
                </Grid>
                { (groupInfo && districtsCount !== null) && (
                    <Grid item xs={12} lg={9} className="group-counter" sx={BoardGroupCounter}>
                        <div className="group-counter__wrapper">
                            <div className="group-counter__versus">{`${referenceDistrict.label} - vs -`} <GroupDisplay groupData={{ groupInfo: groupInfo, groupType: groupType }} /></div>
                            <div className="group-counter__result">{`Year: ${year.value === "MCD"
                                ? `2026 (${year.label})`
                                : year.label
                                } | ${districtsCount} districts in the comparison group.`}</div>
                        </div>
                    </Grid>
                )}
            </Grid>
        </Box>
    );

    return (
        <LayoutPage
            pageTitle={<div>Salary Toolkit <span style={{color: "#FFDF00"}}>(Beta)</span></div>}
            documentTitle="Salary Toolkit (Beta)"
            description="Salary Toolkit"
            type="dashboard"
            customClass="salary_toolkit"
            topContent={topContentElement}
        >
            <Box sx={SalaryDashboardStyles}>
                <Grid container spacing={2}>
                    {
                        boardData &&
                        <Grid item xs={12}>
                            <Paper className='toggle-acad-level'>
                                <span>Academic level: </span>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={toggleAcadLevel ? 'MA' : 'BA'}
                                    exclusive
                                    onChange={handleAcademicLevel}
                                    aria-label="Platform"
                                >
                                    <ToggleButton value="BA">Bachelor (BA)</ToggleButton>
                                    <ToggleButton value="MA">Master (MA)</ToggleButton>
                                </ToggleButtonGroup>
                                <div>{toggleAcadLevel}</div>
                            </Paper>
                        </Grid>
                    }
                    { (salaryDataBa && salaryDataMa) &&
                        <Grid item xs={12} lg={6}>
                            <DistrictCard data={toggleAcadLevel ? salaryDataMa : salaryDataBa} />
                        </Grid>
                    }
                    {(referenceDistrict && year) && (
                        <>
                            <Grid item xs={12} lg={6}>
                                {
                                    salaryOptions &&
                                    <Select
                                        value={salaryVariable}
                                        onChange={handleChange}
                                        sx={{ width: "100%" }}
                                    >
                                        {salaryOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                }
                                {
                                    mapData &&
                                    <MapView data={mapData} districtIrn={referenceDistrict ? referenceDistrict.value : null} height="484px" zoom={7} choroplethVar={salaryVariable} type={ toggleAcadLevel ? 'salary_ma' : 'salary_ba'} year={year} />

                                }
                            </Grid>
                            {
                                // Reference District Salary Historical Data is null.
                                (districtHistoricalDataNull || groupHistoricalDataNull || !districtsCount) &&
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        No data available for
                                        {
                                            districtHistoricalDataNull &&
                                            <strong> {referenceDistrict.label} </strong>
                                        }
                                        {
                                            (districtHistoricalDataNull && groupHistoricalDataNull) && ' and '
                                        }
                                        {
                                            groupHistoricalDataNull &&
                                            <strong> {groupInfo.length > 1 ? ' any district in the comparison' : groupInfo[0]?.label} group </strong>
                                        }
                                        {
                                            !districtsCount &&
                                            <strong> any district in the group with the filter(s) applied </strong>
                                        }
                                        in this period.
                                    </Alert>
                                </Grid>
                            }
                            {lineData && !loading && (
                                <Grid item xs={12}>
                                    <Paper sx={{ padding: "15px" }}>
                                        <div>
                                            {
                                                districtsCount > 0 &&
                                                <Button color="purple" variant="contained" onClick={() => setHighDensity(!highDensity)}>Display {highDensity ? 'Low Density' : 'High Density'}</Button>
                                            }
                                            { highDensity ? (
                                                <ErrorBarsChart data={errorBar} />
                                            ) : (
                                                <LineChart data={lineData} labelPoints={false} chartHeight={530} title={"Salary Indexes"} max={37} currencyValue={true} />
                                            )}
                                        </div>
                                        {
                                            nullDistricts.length > 0 &&
                                            <div className='nulls-block'>
                                                <Accordion defaultExpanded={true} sx={nullsBlockAccordion}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls="panel1a-content"
                                                        id="panel1a-header"
                                                    >
                                                        <div className='nulls-block__message'>No data available:</div>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <ul className='nulls-block__districts'>
                                                            {
                                                                nullDistricts.map((district, key) => <li key={key} className='null-district'> {district.name}</li>)
                                                            }
                                                        </ul>
                                                    </AccordionDetails>
                                                </Accordion>
                                            </div>
                                        }
                                    </Paper>
                                </Grid>
                            )}
                            { // Axis Accumulated lollipop chart.
                                (histogramAccumulated) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varAccumulated?.label}
                                        data={histogramAccumulated}
                                        description={varAccumulated?.description}
                                        id={"histogram-accumulated"}
                                        ranges={statsAccumulated}
                                    />
                                </Grid>
                            }
                            { // Axis Speed lollipop chart.
                                (histogramSpeed) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varSpeed?.label}
                                        data={histogramSpeed}
                                        description={varSpeed?.description}
                                        id={"histogram-speed"}
                                        ranges={statsSpeed}
                                    />
                                </Grid>
                            }
                            { // Axis Base Salary lollipop chart.
                                (histogramBaseSalary) &&
                                <Grid item xs={12} lg={4} className="lollipop_section">
                                    <LollipopChart
                                        title={varBaseSalary?.label}
                                        data={histogramBaseSalary}
                                        description={varBaseSalary?.description}
                                        id={"histogram-base-salary"}
                                        ranges={statsBaseSalary}
                                    />
                                </Grid>
                            }
                            {
                                (referenceDistrict && year) &&
                                <Grid className="custom-groups__filters" item xs={12}>
                                    <MultiVariableFilter
                                        year={year}
                                        referenceDistrict={referenceDistrict}
                                        disabled={false}
                                        onApply={getFiltersInformation.current}
                                        irns={irnsRef.current}
                                        limit={2}
                                        withMap={false}
                                        returnMapData={true}
                                        withSalaryVars={false}
                                    />
                                </Grid>
                            }
                            { (salaryTableDataMa && salaryTableDataBa) &&
                                <>
                                    <DataGridTable
                                        columns= { toggleAcadLevel ? salaryTableDataMa.columns : salaryTableDataBa.columns}
                                        rows={ toggleAcadLevel ? salaryTableDataMa.rows : salaryTableDataBa.rows }
                                        trim={false}
                                    />
                                </>
                            }
                        </>
                    )}
                </Grid>
            </Box>
            <ReportButtons
                boardType={"Board Salary Toolkit"}
                collectedData={{
                    referenceDistrict: referenceDistrict,
                    irns: irns,
                    groupInfo: groupInfo,
                    groupType: groupType,
                    groupCount: districtsCount,
                    year: {value: year.value, label: year.value},
                    vars: varsFilters,
                }}
            />
        </LayoutPage>
    );
}

export default SalaryDashboard;
